import pginterval, { type IPostgresInterval } from "postgres-interval";

import dayjs from "@/providers/dates";
import { getRoundedToPrecision } from "@/utils/format";

import type { Duration, DurationUnitType } from "dayjs/plugin/duration";

// Types
export type StringDate = string | Date;
export type DateFormat =
  | "day"
  | "datetime"
  | "datetime-short"
  | "date"
  | "date-short"
  | "date-short-file"
  | "date-very-short"
  | "time"
  | "time-short"
  | "week-short"
  | "month-short";
export const DATE_FORMATS: Record<DateFormat, string> = {
  day: "D",
  datetime: "MMM D, YYYY HH:mm", // -> Dec 11, 2024 00:00
  "datetime-short": "DD.MM. HH:mm", // -> 11.12. 00:00
  date: "MMM D, YYYY", // -> Dec 11, 2024
  "date-short": "DD.MM.YY", // -> 11.12.24
  "date-short-file": "DD-MM-YYYY", // -> 11-12-2024
  "date-very-short": "DD.MM.", // -> 11.12.
  time: "HH:mm:ss", // 00:00:00
  "time-short": "HH:mm", // 00:00
  "week-short": "w", // 26
  "month-short": "MMM YY", // Jan 2024
};
const formatDateDuration = (duration: Duration): string => {
  const formattedDuration = duration.format("Y[y] M[mo] D[d] H[h] m[m] s[s]");
  const escapedDuration = formattedDuration
    .replace(/\b0y\b/, "")
    .replace(/\b0mo\b/, "")
    .replace(/\b0d\b/, "")
    .replace(/\b0h\b/, "")
    .replace(/\b0m\b/, "")
    .replace(/\b0s\b/, "");
  return escapedDuration.trim();
};
export type DateDurationObject = Omit<
  IPostgresInterval,
  "toISO" | "toISOString" | "toISOStringShort" | "toPostgres" | "toTemporalDuration"
>;
export const DEFAULT_DATE_DURATION_OBJECT: DateDurationObject = {
  years: 0,
  months: 0,
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  milliseconds: 0,
};

// Helpers
export const isDateValid = (date?: StringDate | null): boolean => (date ? dayjs(date).isValid() : false);
// Day-granularity comparisons run in local time: dayjs computes the day boundary in the receiver's mode,
// so .utc() would put local-midnight dates on the
// previous calendar day for every timezone east of Greenwich.
export const getDateIsToday = (date: StringDate): boolean => dayjs(date).isSame(dayjs(), "day");
export const getDateIsSameDay = (date_1: StringDate, date_2: StringDate): boolean =>
  dayjs(date_1).isSame(dayjs(date_2), "day");
export const getDatePlusMinus = (date?: StringDate, add = 0, unit: dayjs.ManipulateType = "day"): string =>
  dayjs(date).add(add, unit).toISOString();
export const getDateIsBetween = (date: StringDate, minDate: StringDate, maxDate: StringDate): boolean =>
  dayjs(date).isBetween(dayjs(minDate), dayjs(maxDate), "millisecond", "[]");
export const getDateIsBefore = (date_1: StringDate, date_2: StringDate): boolean =>
  dayjs(date_1).isBefore(dayjs(date_2));
export const getDateIsAfter = (date_1: StringDate, date_2: StringDate): boolean => dayjs(date_1).isAfter(dayjs(date_2));
export const getDateIsSameOrAfter = (date_1: StringDate, date_2: StringDate): boolean =>
  dayjs(date_1).isSameOrAfter(dayjs(date_2));
export const getDateIsSameOrBefore = (date_1: StringDate, date_2: StringDate): boolean =>
  dayjs(date_1).isSameOrBefore(dayjs(date_2));
export const getDateMin = (dates: StringDate[]) => {
  const dayMin = dayjs.min(dates.map((date) => dayjs(date)));
  if (dayMin) return dayMin.toISOString();
  return undefined;
};
export const getDateMax = (dates: StringDate[]) => {
  const dayMax = dayjs.max(dates.map((date) => dayjs(date)));
  if (dayMax) return dayMax.toISOString();
  return undefined;
};

// Specific Dates
export const getDateISO = (date?: StringDate): string => dayjs(date).toISOString();
export const getDateISOfromUTC = (date?: StringDate): string => dayjs.utc(date).toISOString();
export const getDateJS = (date?: StringDate): Date => dayjs(date).toDate();
export const getDateJSfromUTC = (date?: StringDate): Date => dayjs.utc(date).toDate();
export const getDateUnixTime = (date: StringDate): number => dayjs(date).unix();
export const getDateNow = (): string => getDateISO();
export const getDateStartOf = (date?: StringDate, unit: dayjs.OpUnitType = "day"): string =>
  dayjs(date).startOf(unit).utc().toISOString();
export const getDateEndOf = (date?: StringDate, unit: dayjs.OpUnitType = "day"): string =>
  dayjs(date).endOf(unit).utc().toISOString();
export const getDateStartOfDay = (date?: StringDate): string => dayjs(date).startOf("day").utc().toISOString();
export const getDateStartOfTomorrow = (): string => getDateStartOfDay(getDatePlusMinus(getDateNow(), 1));
export const getDateEndOfDay = (date?: StringDate): string => dayjs(date).endOf("day").utc().toISOString();
export const getDateMiddayOfDay = (date?: StringDate): string => dayjs(date).set("hour", 12).utc().toISOString();
export const getDateDaysInThePast = (days = 30, date?: StringDate | null): string =>
  dayjs(date).subtract(days, "days").utc().toISOString();
export const getDateDaysInTheFuture = (days = 30, date?: StringDate | null): string =>
  dayjs(date).add(days, "days").utc().toISOString();

// ! durations
export const getDurationObjectFromInterval = (interval?: string | null): DateDurationObject =>
  interval ? pginterval(interval) : DEFAULT_DATE_DURATION_OBJECT;
export const getIntervalFromDurationObject = (values: DateDurationObject): string | undefined => {
  if (getDateDurationByObject(values) === 0) return undefined;

  // simulate what a postgres interval string would look like for the given duration
  const parts: string[] = [];
  if (values.years) parts.push(`${values.years} year${values.years !== 1 ? "s" : ""}`);
  if (values.months) parts.push(`${values.months} month${values.months !== 1 ? "s" : ""}`);
  if (values.days) parts.push(`${values.days} day${values.days !== 1 ? "s" : ""}`);
  const hh = String(values.hours).padStart(2, "0");
  const mm = String(values.minutes).padStart(2, "0");
  const ss = String(values.seconds).padStart(2, "0");
  parts.push(`${hh}:${mm}:${ss}`);
  return parts.join(" ");
};

export const getDurationObjectFromMs = (ms: number): DateDurationObject => {
  const d = dayjs.duration(ms);
  return {
    years: 0,
    months: 0,
    days: d.days(),
    hours: d.hours(),
    minutes: d.minutes(),
    seconds: d.seconds(),
    milliseconds: d.milliseconds(),
  };
};

export const getDurationStringFromDates = (date_1: StringDate, date_2: StringDate): string => {
  return getIntervalFromDurationObject(getDurationObjectFromMs(getDateDurationAs(date_1, date_2))) ?? "";
};

export const getDateDurationByObject = (props: DateDurationObject): number => dayjs.duration(props).asMilliseconds();
export const getDateDurationByInterval = (interval?: string | null): number =>
  getDateDurationByObject(getDurationObjectFromInterval(interval));

// ! duration by dates
export const getDateDurationExact = (date_1: StringDate, date_2: StringDate): string => {
  const duration = dayjs.duration(Math.abs(dayjs(date_1).diff(dayjs(date_2))));
  return formatDateDuration(duration);
};
export const getDateDurationHumanized = (date_1: StringDate, date_2: StringDate): string =>
  dayjs.duration(Math.abs(dayjs(date_1).diff(dayjs(date_2)))).humanize();
export const getDateDurationAs = (
  date_1: StringDate,
  date_2: StringDate,
  format: DurationUnitType = "milliseconds",
  roundToPrecision?: number,
): number => {
  const duration = dayjs.duration(Math.abs(dayjs(date_1).diff(dayjs(date_2))));
  return getRoundedToPrecision(duration.as(format), roundToPrecision);
};

// ! duration by Milliseconds
export const getDateDurationMsExact = (milliseconds?: number | null): string | null => {
  if (!milliseconds) return null;
  const duration = dayjs.duration(Math.abs(milliseconds));
  return formatDateDuration(duration);
};
export const getDateDurationMsHumanized = (milliseconds?: number | null): string | null => {
  if (!milliseconds) return null;
  return dayjs.duration(Math.abs(milliseconds)).humanize();
};
export const getDateDurationMsAs = (
  milliseconds?: number | null,
  format: DurationUnitType = "milliseconds",
  roundToPrecision?: number,
): number | null => {
  if (!milliseconds) return null;
  const duration = dayjs.duration(Math.abs(milliseconds));
  return getRoundedToPrecision(duration.as(format), roundToPrecision);
};
// ! duration by interval
export const getDateDurationIntervalExact = (interval?: string | null): string | null => {
  return getDateDurationMsExact(getDateDurationByInterval(interval));
};
export const getDateDurationIntervalHumanized = (interval?: string | null): string | null => {
  return dayjs.duration(getDateDurationByInterval(interval)).humanize();
};
export const getDateDurationIntervalAs = (
  interval?: string | null,
  format: DurationUnitType = "milliseconds",
  roundToPrecision?: number,
): number | null => {
  const duration = dayjs.duration(getDateDurationByInterval(interval));
  return getRoundedToPrecision(duration.as(format), roundToPrecision);
};

export const getDateMsDifference = (date_1: StringDate, date_2: StringDate, float = true): number =>
  dayjs(date_1).diff(dayjs(date_2), "millisecond", float);
export const getDateDaysDifference = (date_1: StringDate, date_2: StringDate, float = true): number =>
  dayjs(date_1).diff(dayjs(date_2), "day", float);
export const getDateDaysDifferenceToNow = (date: StringDate): number => dayjs(date).diff(getDateNow(), "day", true);

// Mantine Picker Conversion
export const isMantineDate = (date: StringDate): boolean => dayjs(date, "YYYY-MM-DD", true).isValid();
export const isMantineDateTime = (date: StringDate): boolean => dayjs(date, "YYYY-MM-DD HH:mm:ss", true).isValid();
/**
 * Convert UTC ISO from database to Mantine DatePicker format (local date)
 * "2024-03-15T22:30:00.000Z" → "2024-03-15" (in user's timezone)
 */
export function isoToMantineDate(date: StringDate): string {
  return dayjs(date).format("YYYY-MM-DD");
}

/**
 * Convert UTC ISO from database to Mantine DateTimePicker format (local datetime)
 * "2024-03-15T22:30:00.000Z" → "2024-03-15 14:30:00" (in PST, for example)
 */
export function isoToMantineDateTime(date: StringDate): string {
  return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
}

/**
 * Convert Mantine DatePicker string to UTC ISO for database
 * User's local "2024-03-15" → "2024-03-15T07:00:00.000Z" (midnight in their TZ)
 */
export function mantineDateToISO(date: StringDate): string {
  // Parse as local date, get start of day in local TZ, convert to UTC
  return dayjs(date, "YYYY-MM-DD").startOf("day").utc().toISOString();
}

/**
 * Convert Mantine DateTimePicker string to UTC ISO for database
 * User's local "2024-03-15 14:30:00" → "2024-03-15T22:30:00.000Z" (if PST)
 */
export function mantineDateTimeToISO(date: StringDate): string {
  // Parse as local datetime, convert to UTC
  return dayjs(date, "YYYY-MM-DD HH:mm:ss").utc().toISOString();
}

// Formatting
export const getDateFormatted = (date: StringDate | dayjs.Dayjs, format: DateFormat = "date-short") =>
  dayjs(date).format(DATE_FORMATS[format]);

export const isDateInDateRangeOrAfterMinDateOrBeforeMaxDate = ({
  date,
  minDate,
  maxDate,
}: {
  date: StringDate;
  minDate?: StringDate;
  maxDate?: StringDate;
}): boolean => {
  if (minDate && maxDate) {
    return getDateIsBetween(date, minDate, maxDate);
  }
  if (minDate) {
    return getDateIsSameOrAfter(date, minDate);
  }
  if (maxDate) {
    return getDateIsSameOrBefore(date, maxDate);
  }
  return true;
};

interface GenerateDayDateArrayParams {
  from: StringDate;
  to?: StringDate;
}
export function generateDayDateArray({ from, to }: GenerateDayDateArrayParams): string[] {
  const _from = getDateMiddayOfDay(from);
  const _to = to ? getDateEndOfDay(to) : getDateEndOfDay();

  const dates: string[] = [];
  for (let date = _from; getDateIsBefore(date, _to); date = getDatePlusMinus(date, 1, "day")) {
    dates.push(date);
  }

  return dates;
}

// Timezones
export const getDateInTimezone = (date: StringDate, timezone: string, format = DATE_FORMATS.datetime) =>
  dayjs(date).tz(timezone).format(format);

export const getTimezoneOffset = (timezone: string) => dayjs.tz(getDateNow(), timezone).utcOffset();

export const getTimezoneOffsetLabel = (timezone: string) => {
  const offset = getTimezoneOffset(timezone);
  const prefix = Math.sign(offset) >= 0 ? "+" : "";
  const hourOffset = `${Math.floor(offset / 60)}`;
  const minuteOffset = offset % 60 === 0 ? "00" : String(Math.abs(offset % 60)).padEnd(2, "0");
  return `(GMT${prefix}${hourOffset}:${minuteOffset})`;
};

export const getSupportedTimezones = () => Intl.supportedValuesOf("timeZone");

export const getSupportedTimezonesValueLabel = () => {
  const timezones: Record<"value" | "label", string>[] = Intl.supportedValuesOf("timeZone").map((timezone) => ({
    value: timezone,
    label: `${timezone} ${getTimezoneOffsetLabel(timezone)}`,
  }));
  // remove UTC from the list, if it exists because we handle it separately
  const filteredTimezones = timezones.filter((timezone) => timezone.value !== "UTC");
  filteredTimezones.unshift({ value: "UTC", label: "UTC (GMT+0:00)" });
  return filteredTimezones;
};
